import { alpha, Box, Paper, styled, Typography, useTheme, Button, Collapse, IconButton } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { flagSpain, flagUnitedStates, flagBrazil } from '@frontend/assets';
import { useParams } from "react-router-dom";
import { useGetHotelNimbuxHotelById, useGetHotelVendorHotelDescriptions, useGetModeratedVendorHotelsById } from "@frontend/features/hotel-content-manager/queries";
import { usePatchHotel } from "@frontend/features/hotel-content-manager/mutations";
import { QueryKeys } from "@frontend/features/common/types";
import { useSnackBar } from "@frontend/features/common/components/SnackBarContext";
import { useQueryClient } from "react-query";
import { DescriptionType } from "@bandido/shared/enums";

type Language = 'es' | 'en' | 'pt';

interface LanguageConfig {
  code: Language;
  flag: string;
  name: string;
}

const languages: LanguageConfig[] = [
  { code: 'es', flag: flagSpain, name: 'Español' },
  { code: 'en', flag: flagUnitedStates, name: 'English' },
  { code: 'pt', flag: flagBrazil, name: 'Português' },
];

const LanguageEditorHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const GenerateButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightMedium,
}));

const LanguageEditorContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));


const EditorBox = styled(Box)(({ theme }) => ({
  width: '100%',


  '& .ck-editor': {
    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  '& .ck-editor__main': {
    minHeight: '150px',
    border: 'none',
  },
  '& .ck-content': {
    minHeight: '120px',
    padding: theme.spacing(2),
    border: 'none',
  },
  '& .ck.ck-editor__top .ck-sticky-panel .ck-sticky-panel__content': {
    border: 'none',
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),

  },
  '& .ck.ck-editor__editable.ck-focused:not(.ck-editor__nested-editable)': {
    border: 'none',
  },
  '& .ck.ck-toolbar>.ck-toolbar__items ': {
    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    display: 'flex',
    flexGrow: 'inherit',
    borderRadius: theme.shape.borderRadius,
  },

  '& .ck-button': {
    color: theme.palette.text.secondary,
  },
  '& .ck-button:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .ck-button.ck-on': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  }
}));

const MultiLanguageContentEditor = ({
  values,
  onChange,
  placeholder,
  onGenerateWithAI
}: {
  values: Record<Language, string>;
  onChange: (language: Language, data: string) => void;
  placeholder: string;
  onGenerateWithAI?: (language: Language) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      {languages.map((lang) => (
        <LanguageEditorContainer key={lang.code}>
          <LanguageEditorHeader>
            <img style={{ width: '20px', height: '15px' }} src={lang.flag} alt={lang.name} />
            {onGenerateWithAI && (
              <GenerateButton
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => onGenerateWithAI(lang.code)}
              >
                {t('hotelContent.contentEditor.generateWithAI')}
              </GenerateButton>
            )}
          </LanguageEditorHeader>

          <EditorBox>
            <CKEditor
              editor={ClassicEditor as any}
              data={values[lang.code] || ''}
              onChange={(_: any, editor: any) => {
                const data = editor.getData();
                onChange(lang.code, data);
              }}
              config={{
                placeholder: `${placeholder} (${lang.name})`,
                toolbar: {
                  items: [
                    'undo',
                    'redo',
                    '|',
                    'heading',
                    '|',
                    'bold',
                    'italic',
                    'underline',
                    '|',
                    'bulletedList',
                    'numberedList',
                    '|',
                    'outdent',
                    'indent',
                    '|',
                    'link',
                  ]
                },
                heading: {
                  options: [
                    { model: 'paragraph', title: t('hotelContent.contentEditor.ckeditor.paragraph'), class: 'ck-heading_paragraph' },
                    { model: 'heading1', view: 'h1', title: t('hotelContent.contentEditor.ckeditor.heading1'), class: 'ck-heading_heading1' },
                    { model: 'heading2', view: 'h2', title: t('hotelContent.contentEditor.ckeditor.heading2'), class: 'ck-heading_heading2' },
                    { model: 'heading3', view: 'h3', title: t('hotelContent.contentEditor.ckeditor.heading3'), class: 'ck-heading_heading3' },
                    { model: 'heading4', view: 'h4', title: t('hotelContent.contentEditor.ckeditor.heading4'), class: 'ck-heading_heading4' }
                  ]
                }
              }}
            />
          </EditorBox>
        </LanguageEditorContainer>
      ))}
    </Box>
  );
};

const ProviderContent = ({ supplierName, descriptions }: { supplierName: string, descriptions?: any[] }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);

  const generalDescriptions = descriptions?.filter((desc) => desc.type === DescriptionType.GENERAL) || [];
  
  const getDescriptionByLanguage = (language: Language) => {
    if (generalDescriptions.length === 0) return '';
    
    const [ firstGeneralDesc ] = generalDescriptions;
    const textForLanguage = firstGeneralDesc.texts?.find((text: any) => text.language === language);
    return textForLanguage?.text || '';
  };

  return (
    <Box
      display='flex'
      flexDirection='column'
      gap={3}
      p={2}
      borderRadius={1}
      sx={{ backgroundColor: theme.palette.paper.dark }}
      alignItems="flex-start"
      alignSelf="stretch"
    >
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        
        p={theme.spacing(0.5, 1.5)}
        borderRadius={1}
        sx={{ backgroundColor: theme.palette.secondary.states?.outlinedBorder }}
      >
        <Typography variant="subtitle2" fontWeight="medium">
          {supplierName}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label={isExpanded ? t('hotelContent.contentEditor.collapseContent') : t('hotelContent.contentEditor.expandContent')}
        >
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded} sx={{ width: '100%' }}>
        <Box display='flex' flexDirection='column' gap={3} width='100%'>
          {languages.map((lang) => {
            const content = getDescriptionByLanguage(lang.code);
            if (!content) return null;

            return (
              <Box key={lang.code} display='flex' flexDirection='column' gap={1}>
                <Box display='flex' alignItems='center' gap={1}>
                  <img 
                    style={{ width: '20px', height: '15px' }} 
                    src={lang.flag} 
                    alt={lang.name} 
                  />
                  <Typography variant="subtitle2" fontWeight="medium">
                    {lang.name}
                  </Typography>
                </Box>
                
                <Box
                  sx={{
                    all: 'initial',
                    fontFamily: theme.typography.fontFamily,
                    fontSize: theme.typography.body2.fontSize,
                    lineHeight: theme.typography.body2.lineHeight,
                    color: theme.palette.text.primary,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    width: '100%',
                    '& div': {
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      fontFamily: theme.typography.fontFamily,
                      fontWeight: theme.typography.h6.fontWeight,
                      color: theme.palette.text.primary,
                      margin: theme.spacing(0.5, 0),
                    },
                    '& h3': {
                      fontSize: theme.typography.h6.fontSize,
                    },
                    '& p': {
                      fontFamily: theme.typography.fontFamily,
                      fontSize: theme.typography.body2.fontSize,
                      lineHeight: theme.typography.body2.lineHeight,
                      color: theme.palette.text.secondary,
                      margin: theme.spacing(0.25, 0),
                    },
                    '& ul, & ol': {
                      fontFamily: theme.typography.fontFamily,
                      fontSize: theme.typography.body2.fontSize,
                      color: theme.palette.text.secondary,
                      paddingLeft: theme.spacing(2),
                      margin: theme.spacing(0.25, 0),
                    },
                    '& li': {
                      margin: theme.spacing(0.125, 0),
                    },
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: content 
                  }}
                />
              </Box>
            );
          })}
          
          {languages.every(lang => !getDescriptionByLanguage(lang.code)) && (
            <Typography variant="body2" color="text.secondary" style={{ fontStyle: 'italic' }}>
              {t('hotelContent.contentEditor.noDescriptionsAvailable')}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

export const HotelContentPerse = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { addToast } = useSnackBar();
  const { hotelId } = useParams();
  const { data: nimbuxHotel } = useGetHotelNimbuxHotelById(Number(hotelId));
  const { data: moderatedVendorHotels } = useGetModeratedVendorHotelsById(Number(hotelId));
  const vendorHotelDescriptionParams = moderatedVendorHotels?.map((hotel) => ({
    accessCode: hotel.accessCode || '',
    hotelCode: hotel.code || ''
  }));
  const { data: vendorHotelDescriptions, isLoading: isLoadingVendorHotelDescriptions } = useGetHotelVendorHotelDescriptions(vendorHotelDescriptionParams);
  
  const { mutate: updateHotelDescriptions, isLoading: isUpdateingHotelDescriptions } = usePatchHotel(
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.getHotelNimbuxHotelById, Number(hotelId)]);
        handleSuccessfulSave();
        addToast({
          color: 'success',
          message: t('hotelContent.view.amenities.messages.updateSuccess'),
        });
      },
      onError: () => {
        addToast({
          color: 'error',
          message: t('hotelContent.view.amenities.messages.updateError'),
        });
      },
    }
  );

  const [contentValues, setContentValues] = useState<Record<Language, string>>({
    es: '',
    en: '',
    pt: ''
  });

  const [originalValues, setOriginalValues] = useState<Record<Language, string>>({
    es: '',
    en: '',
    pt: ''
  });

  const [hasInitialized, setHasInitialized] = useState(false);

  const isDirty = hasInitialized && JSON.stringify(contentValues) !== JSON.stringify(originalValues);

  useEffect(() => {
    if (nimbuxHotel?.descriptions !== undefined) {
      const generalDescriptions = nimbuxHotel.descriptions.filter(desc => desc.type === DescriptionType.GENERAL);
      
      if (generalDescriptions.length > 0) {
        const [ generalContent ] = generalDescriptions;
        
        const newContentValues = {
          es: generalContent.texts.es || '',
          en: generalContent.texts.en || '',
          pt: generalContent.texts.pt || ''
        };

        setContentValues(newContentValues);
        setOriginalValues(newContentValues);
      } else {
        // Si no hay descripción general, establecer valores vacíos
        const emptyValues = { es: '', en: '', pt: '' };
        setContentValues(emptyValues);
        setOriginalValues(emptyValues);
      }
      setHasInitialized(true);
    }
  }, [nimbuxHotel]);

  const handleContentChange = (language: Language, data: string) => {
    setContentValues(prev => ({
      ...prev,
      [language]: data
    }));
  };

  const handleGenerateWithAI = (language: Language) => {
    // TODO: Implementar lógica de generación con IA
    console.log('Generar contenido con IA para:', language);
  };

  const handleSaveDescriptions = () => {
    if (!nimbuxHotel) return;

    const updatedDescriptions = [...(nimbuxHotel.descriptions || [])];
    const generalDescIndex = updatedDescriptions.findIndex(desc => desc.type === DescriptionType.GENERAL);
    
    const newGeneralDescription = {
      type: DescriptionType.GENERAL,
      texts: {
        es: contentValues.es || '',
        en: contentValues.en || '',
        pt: contentValues.pt || ''
      }
    };

    if (generalDescIndex >= 0) {
      updatedDescriptions[generalDescIndex] = newGeneralDescription;
    } else {
      updatedDescriptions.push(newGeneralDescription);
    }

    updateHotelDescriptions({
      id: Number(hotelId),
      descriptions: updatedDescriptions
    });
  };

  const handleSuccessfulSave = () => {
    // Actualizar los valores originales después de un guardado exitoso
    setOriginalValues({ ...contentValues });
  };

  return (
    <Paper elevation={0} sx={{ mb: 8, maxWidth: 1082 }}>
      <Box display='flex' flexDirection='column' gap={5} p={2} borderRadius={2} >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('hotelContent.contentEditor.title')}</Typography>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleSaveDescriptions}
            loading={isUpdateingHotelDescriptions}
            disabled={!isDirty || isUpdateingHotelDescriptions}
            sx={{ textTransform: 'none' }}
          >
            {t('common.save')}
          </LoadingButton>
        </Box>

        <Box display="flex" flexDirection="column" gap={3}>
          <Typography variant="subtitle2">{t('hotelContent.contentEditor.editorTitle')}</Typography>
          <MultiLanguageContentEditor
            values={contentValues}
            onChange={handleContentChange}
            placeholder={t('hotelContent.contentEditor.placeholder')}
            onGenerateWithAI={handleGenerateWithAI}
          />
        </Box>

        <Box display='flex' flexDirection='column' gap={3} borderRadius={2} >
          <Typography variant="subtitle2">{t('hotelContent.contentEditor.suppliersPreview')}</Typography>
          <Box display='flex' flexDirection='column' gap={5} borderRadius={2} >
            {vendorHotelDescriptions && vendorHotelDescriptions.length > 0 ? (
              vendorHotelDescriptions.map((vendorDescription, index) => {
                const matchingVendorHotel = moderatedVendorHotels?.find(
                  hotel => hotel.accessCode === vendorDescription.accessCode
                );
                
                return (
                  <ProviderContent
                    key={index}
                    supplierName={matchingVendorHotel?.supplierName || vendorDescription.accessCode}
                    descriptions={vendorDescription.descriptions}
                  />
                );
              })
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {isLoadingVendorHotelDescriptions 
                    ? t('hotelContent.contentEditor.loadingDescriptions')
                    : t('hotelContent.contentEditor.noDescriptionsFromSuppliers')
                  }
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};
